import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  LifeBuoy,
  Search,
  ChevronDown,
  Mail,
  KeyRound,
  CreditCard,
  Smartphone,
  ShieldCheck,
  Sparkles,
  Leaf,
} from "lucide-react";

export const Route = createFileRoute("/help")({
  head: () => ({
    meta: [
      { title: "Help & Support — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Answers to common questions about your account, billing, devices, and how to use Recovery Trauma™.",
      },
      { property: "og:title", content: "Help & Support — Recovery Trauma™" },
      { property: "og:description", content: "Answers to common questions about your account, billing, devices, and how to use Recovery Trauma™." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/help" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/help" }],
  }),
  component: HelpPage,
});

type Article = {
  id: string;
  category: string;
  q: string;
  a: string;
  /** Optional one-tap shortcut shown inside the expanded answer. */
  action?: { to: string; label: string };
};

const CATEGORIES = [
  { id: "account", label: "Account & Login", icon: KeyRound },
  { id: "billing", label: "Billing & Subscription", icon: CreditCard },
  { id: "devices", label: "Devices & Compatibility", icon: Smartphone },
  { id: "safety", label: "Safety & Wellbeing", icon: ShieldCheck },
  { id: "using", label: "Using the App", icon: Sparkles },
] as const;

const ARTICLES: Article[] = [
  // Account
  {
    id: "reset-password",
    category: "account",
    q: "How do I reset my password?",
    a: "Go to the login screen and tap 'Forgot password'. Enter the email you signed up with — we'll send a secure reset link. The link expires after 1 hour. If you don't see it, check your spam folder.",
  },
  {
    id: "login-fail",
    category: "account",
    q: "I can't log in — what should I try?",
    a: "1) Confirm you're using the same email you signed up with. 2) Reset your password if unsure. 3) Clear your browser cache or try a private window. If it still fails, contact support.",
  },
  {
    id: "change-email",
    category: "account",
    q: "How do I change my email address?",
    a: "Go to Profile → Account settings. You'll be asked to confirm the new email via a verification link.",
  },
  {
    id: "delete-account",
    category: "account",
    q: "How do I delete my account?",
    a: "Profile → Account settings → Delete account. This permanently removes your journal entries, self-reflections, and community posts within 30 days. Active subscriptions are cancelled automatically.",
    action: {
      to: "/account/privacy",
      label: "Delete my account",
    },
  },

  // Billing
  {
    id: "cancel-sub",
    category: "billing",
    q: "How do I cancel my subscription?",
    a: "Profile → Membership → Cancel subscription. You'll keep access until the end of the current billing period. No partial refunds for unused time, but you can re-activate anytime.",
  },
  {
    id: "payment-fail",
    category: "billing",
    q: "My payment failed. What now?",
    a: "Common causes: expired card, insufficient funds, or 3-D Secure verification. Update your payment method in Profile → Membership → Payment. We'll automatically retry the charge for 7 days before pausing your subscription.",
  },
  {
    id: "refund",
    category: "billing",
    q: "Can I get a refund?",
    a: "How a refund is handled depends on where you purchased your membership. If you purchased through the website, email info@recoverytrauma.com with the email address on your membership and the reason for your request. If you purchased through Apple, refunds are requested through Apple's own refund process — Recovery Trauma™ cannot refund an Apple transaction directly. If you're not sure where you purchased, check Profile → My Membership for your billing provider. Your statutory rights always apply — see the Requesting a Refund section below, and sections 10 and 11 of our Terms of Service for the full detail.",
  },
  {
    id: "expired-card",
    category: "billing",
    q: "My card expired — how do I update it?",
    a: "Profile → Membership → Payment method → Update card. New charges will use the updated card immediately.",
  },
  {
    id: "reactivate",
    category: "billing",
    q: "How do I re-activate after cancelling?",
    a: "Profile → Membership → Renew. Your previous progress, journal entries, and saved tools are preserved.",
  },

  // Devices
  {
    id: "browsers",
    category: "devices",
    q: "Which browsers and devices are supported?",
    a: "Recovery Trauma™ works on the latest versions of Chrome, Safari, Edge, and Firefox on desktop, tablet, and mobile. For the best experience use a modern browser updated within the last 12 months.",
  },
  {
    id: "offline",
    category: "devices",
    q: "Does the app work offline?",
    a: "Most reading content can be cached after first load, but journaling, community, and audio playback require a stable internet connection.",
  },
  {
    id: "audio",
    category: "devices",
    q: "Audio (meditations, sounds) won't play",
    a: "1) Make sure your device isn't on silent. 2) Tap the play button once — browsers block auto-play until you interact. 3) Try headphones. 4) Refresh the page if the player gets stuck.",
  },

  // Safety
  {
    id: "crisis",
    category: "safety",
    q: "I'm in crisis — what should I do?",
    a: "Recovery Trauma™ is NOT a crisis service. If you are in danger or thinking about hurting yourself, please contact your local emergency services or a crisis line immediately. See our resources page for international hotlines.",
  },
  {
    id: "ground-me",
    category: "safety",
    q: "What does 'Ground Me Now' do?",
    a: "It's a always-visible button (bottom-right) that opens quick regulation tools — box breathing, 5-4-3-2-1 grounding, safe-place visualization, body scan, and hand on heart. Use it any time content feels activating.",
  },
  {
    id: "trauma-informed",
    category: "safety",
    q: "Is this a replacement for therapy?",
    a: "No. Recovery Trauma™ provides educational resources, self-guided tools, and peer support. It does not replace professional mental health care, medical treatment, therapy, or crisis services.",
  },

  // Using
  {
    id: "journal-private",
    category: "using",
    q: "Are my journal entries private?",
    a: "Yes. Only you can see your journal entries. They are stored encrypted at rest and protected by row-level security — no other member, including admins, can read them.",
  },
  {
    id: "community-rules",
    category: "using",
    q: "What are the community rules?",
    a: "See our Community Guidelines for the full list. In short: share your own experience, be respectful, no graphic trauma details, no diagnosing others, no crisis intervention requests.",
  },
  {
    id: "notifications",
    category: "using",
    q: "How do I turn notifications on or off?",
    a: "Profile → Notifications. You can choose daily check-in reminders, community replies, and live event alerts independently.",
  },
];

function HelpPage() {
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [activeCat, setActiveCat] = useState<string | "all">("all");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ARTICLES.filter((a) => {
      const matchCat = activeCat === "all" || a.category === activeCat;
      const matchQ =
        !q || a.q.toLowerCase().includes(q) || a.a.toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [query, activeCat]);

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <header className="mb-8 text-center">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <LifeBuoy className="h-3.5 w-3.5" /> Help & Support
        </div>
        <h1 className="font-serif text-4xl tracking-tight text-foreground md:text-5xl">
          How can we help?
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-base leading-relaxed text-muted-foreground">
          Search the most common questions or browse by topic. Still stuck? We're
          here.
        </p>
      </header>

      {/* Getting Started — revisit the member welcome anytime */}
      <Link
        to="/member-welcome"
        className="mb-6 flex items-center justify-between gap-3 rounded-2xl border border-gold/30 bg-gradient-to-br from-amber-50/60 to-rose-50/40 p-5 shadow-sm transition hover:shadow-[var(--shadow-gold)]"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--gradient-gold)] text-xl shadow-[var(--shadow-gold)]">
            💛
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
              Getting Started
            </div>
            <div className="font-serif text-lg text-foreground">
              Welcome to Recovery Trauma™
            </div>
            <div className="text-xs text-muted-foreground">
              Revisit your member onboarding anytime.
            </div>
          </div>
        </div>
        <ChevronDown className="h-5 w-5 -rotate-90 text-muted-foreground" />
      </Link>


      {/* Search */}
      <div className="relative mb-6">
        <Search className="pointer-events-none absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search help articles…"
          aria-label="Search help articles"
          className="w-full rounded-full border border-border bg-card py-3 ps-11 pe-4 text-sm text-foreground placeholder:text-muted-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      {/* Categories */}
      <div className="mb-8 flex flex-wrap gap-2">
        <button
          onClick={() => setActiveCat("all")}
          className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
            activeCat === "all"
              ? "border-transparent bg-foreground text-background"
              : "border-border bg-card text-muted-foreground hover:text-foreground"
          }`}
        >
          All topics
        </button>
        {CATEGORIES.map((c) => {
          const Icon = c.icon;
          const active = activeCat === c.id;
          return (
            <button
              key={c.id}
              onClick={() => setActiveCat(c.id)}
              className={`inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                active
                  ? "border-transparent bg-foreground text-background"
                  : "border-border bg-card text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Articles */}
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
            No articles match "{query}". Try a different keyword or contact
            support below.
          </div>
        ) : (
          filtered.map((a) => {
            const open = openId === a.id;
            return (
              <article
                key={a.id}
                className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : a.id)}
                  className="flex w-full items-center justify-between gap-3 px-5 py-4 text-start"
                >
                  <span className="text-sm font-medium text-foreground">
                    {a.q}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
                      open ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {open && (
                  <div className="border-t border-border/50 bg-background/40 px-5 py-4 text-sm leading-relaxed text-foreground/85">
                    {a.a}
                    {a.action && (
                      <div className="mt-3">
                        <Link
                          to={a.action.to}
                          className="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                        >
                          {a.action.label}
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </article>
            );
          })
        )}
      </div>

      {/* Requesting a Refund */}
      <section
        aria-labelledby="refund-heading"
        className="mt-10 overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
      >
        <div className="border-b border-border/60 px-6 py-5">
          <h2
            id="refund-heading"
            className="font-serif text-2xl text-foreground"
          >
            Requesting a Refund
          </h2>
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
            How a refund is handled depends on where you purchased your
            Recovery Trauma™ membership.
          </p>
        </div>
        <div className="grid gap-px bg-border/50 md:grid-cols-3">
          <div className="bg-card px-6 py-5">
            <h3 className="flex items-center gap-2 font-serif text-lg text-foreground">
              <span aria-hidden="true">🌐</span> Website Membership
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground/85">
              If you purchased your membership through{" "}
              <a
                href="https://www.recoverytrauma.com"
                className="font-medium underline"
              >
                www.recoverytrauma.com
              </a>
              , please contact{" "}
              <a
                href="mailto:info@recoverytrauma.com?subject=Refund%20request"
                className="font-medium underline"
              >
                info@recoverytrauma.com
              </a>
              .
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground/85">
              When signing up, you may have requested immediate access to our
              digital content and expressly agreed for the supply of that
              content to begin during the 14-day cancellation period. Where you
              provided this consent and acknowledged the loss of your
              cancellation right, your statutory 14-day right to cancel may no
              longer apply once the digital content has begun to be supplied.
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground/85">
              Refund requests will still be reviewed where appropriate,
              including where you may have rights under applicable consumer law
              or where the digital content or service is faulty, not as
              described, or has not been supplied as agreed.
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground/85">
              Please include the{" "}
              <strong className="font-semibold text-foreground">
                email address associated with your membership
              </strong>{" "}
              and the reason for your refund request so our team can review it.
            </p>
          </div>
          <div className="bg-card px-6 py-5">
            <h3 className="flex items-center gap-2 font-serif text-lg text-foreground">
              <span aria-hidden="true">🍎</span> Apple App Store Purchase
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground/85">
              If you purchased your membership through Apple, Recovery Trauma™
              cannot directly issue a refund for the Apple transaction.
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground/85">
              Refund requests must be submitted through{" "}
              <a
                href="https://reportaproblem.apple.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium underline"
              >
                Apple's refund process
              </a>{" "}
              and will be assessed by Apple in accordance with its policies and
              applicable consumer rights.
            </p>
          </div>
          <div className="bg-card px-6 py-5">
            <h3 className="font-serif text-lg text-foreground">
              Not sure where you purchased?
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground/85">
              Go to{" "}
              <strong className="font-semibold text-foreground">
                Profile → My Membership
              </strong>{" "}
              to see your billing provider.
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground/85">
              For further information, please see{" "}
              <Link to="/terms" className="font-medium underline">
                Sections 10 and 11 of our Terms &amp; Conditions
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      {/* Contact + Safety panels */}
      <div className="mt-10 grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <div className="mb-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <Mail className="h-3.5 w-3.5" /> Contact support
          </div>
          <h3 className="font-serif text-xl text-foreground">
            Still need help?
          </h3>
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
            Our support team replies within 1 business day. Please include the
            email on your account.
          </p>
          <a
            href="mailto:info@recoverytrauma.com?subject=Support%20request"
            className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Mail className="h-4 w-4" /> Email support
          </a>
        </div>

        <div className="rounded-2xl border border-border bg-[var(--sand,#F7F3EA)] p-6">
          <div className="mb-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <Leaf className="h-3.5 w-3.5" /> Feeling overwhelmed?
          </div>
          <h3 className="font-serif text-xl text-foreground">
            You can pause anytime
          </h3>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground/80">
            Tap the green <strong className="font-semibold">Ground Me Now</strong>{" "}
            button at the bottom of any screen for quick regulation tools, or
            visit{" "}
            <Link to="/resources" className="font-semibold underline">
              emergency resources
            </Link>{" "}
            if you're in crisis.
          </p>
        </div>
      </div>
    </div>
  );
}
